import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { User } from "../../interfaces/user";
import { fetchUser } from "../actions/userActions";
import AuthService from "../../services/AuthService";
import { RootState } from "../store";
import { UserWorkspaceStatus } from "@pl-flight-recorder/web/interfaces/UserWorkspaceStatus";

interface ActiveWorkspace {
  id: number;
  name: string;
  userWorkspaceStatus: UserWorkspaceStatus;
  role: { name: string };
}

export interface UserState {
  user: User | null;
  loading: boolean;
  error: string | null;
  activeWorkspace: ActiveWorkspace | null;
}

const initialState: UserState = {
  user: null,
  loading: false,
  error: null,
  activeWorkspace: null,
};

export const validateStoredUser = createAsyncThunk(
  "user/validateStoredUser",
  async (token: string | undefined, { rejectWithValue }) => {
    try {
      // If token provided, use it, otherwise try localStorage
      const userToken =
        token ||
        (() => {
          const storedUser = localStorage.getItem("pulselabs_user");
          if (!storedUser) return null;
          return JSON.parse(storedUser).token;
        })();

      if (!userToken) return rejectWithValue("No token available");

      const authService = AuthService.getInstance();
      const response = await authService.getUserFromToken(userToken);

      // if (response.defaultWorkspace) {
      //   dispatch(setActiveWorkspaceFromToken({
      //     workspaceId: response.defaultWorkspace.id,
      //     token: userToken
      //   }));
      // }

      return {
        ...response,
        token: userToken,
        organization: response.organization || { id: "0" },
        userId: response.id.toString(),
        picture: response.picture || "",
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    updateUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      localStorage.setItem("pulselabs_user", JSON.stringify(action.payload));
    },
    clearUser: (state) => {
      state.user = null;
      state.activeWorkspace = null;
      localStorage.removeItem("pulselabs_user");
    },
    setActiveWorkspace: (state, action: PayloadAction<ActiveWorkspace>) => {
      state.activeWorkspace = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(validateStoredUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(validateStoredUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        if (action.payload.defaultWorkspace) {
          state.activeWorkspace = {
            id: action.payload.defaultWorkspace.id,
            name: action.payload.defaultWorkspace.name,
            userWorkspaceStatus:
              action.payload.defaultWorkspace.userWorkspaceStatus,
            role: action.payload.defaultWorkspace.role,
          };
        }
        localStorage.setItem("pulselabs_user", JSON.stringify(action.payload));
      })
      .addCase(validateStoredUser.rejected, (state) => {
        state.loading = false;
        state.user = null;
        state.activeWorkspace = null;
        localStorage.removeItem("pulselabs_user");
      });
  },
});

export const selectUserActiveWorkspace = (state: RootState) =>
  state.user.activeWorkspace;

export const selectUser = (state: RootState) => state.user.user;

export const { updateUser, clearUser, setActiveWorkspace } = userSlice.actions;
export default userSlice.reducer;
